import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { f2 } from '../../copyFlow2'
import { v3 } from '../../copyV3'
import { lb } from '../../copyLaneB'
import { CATS, HOME_MILES, NO_FILTERS, dateRange, extraCount, type Filters, type When } from '../../lib/browse'
import { formatRange, nearestArea, searchPlaces, todayYmd, type PlaceResult } from '../../lib/intake'
import { catName } from './Rails'
import { ToggleChip } from '../../ui/Chip'
import { Modal } from '../../ui/Modal'
import { buttonClass } from '../../ui/styles'
import { ChevronDown, Locate, Search, Sliders } from '../../ui/icons'
import { useDesktop, useMedia } from '../../ui/useMedia'
import { TypedDates } from './TypedDates'
import { fb } from '../../copyFeatBrowse'
import { useFlag } from '../../lib/flags'
import { FAMES, type Fame } from '../../lib/browse'
import { WordsFilter } from './WordsFilter'
// Round 3, Lane 3 (R-103): time of day and weekdays, in their own component
import { TimeFilter } from './TimeFilter'
import { useLaneFlag } from '../../lib/laneFlag'

/**
 * Choosing a zip, a postal code, a neighborhood or "Use my location" starts the slider at 10 miles (the server's own
 * default for those), so a typed zip visibly narrows the list; the person can slide on to Any distance. A city keeps
 * Any distance (the server applies no limit to a city). Clearing the area returns to Any distance.
 */
const AREA_MILES = 10
const ZIPLIKE = /^(\d{5}(-\d{4})?|[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d|\d{4,6})$/
/** The distance to use after picking `where` (kind: the suggestion's kind, when one was picked). */
function milesFor(where: string, kind: string | undefined, current: number | null): number | null {
  if (!where.trim()) return null
  if (kind === 'city' || kind === 'la') return current
  if (kind === 'area' || kind === 'near' || ZIPLIKE.test(where.trim())) return current ?? AREA_MILES
  return current
}

/** The When chips in order; `label` names a copy key (lane B's own keys start with lb:). */
const WHENS: [When, string][] = [['any', 'filter.any_time'], ['today', 'filter.today'], ['tomorrow', 'filter.tomorrow'], ['week', 'lb:filter.week'], ['weekend', 'filter.weekend'], ['dates', 'filter.choose_dates']]
const whenText = (key: string) => (key.startsWith('lb:') ? lb(key.slice(3) as 'filter.week') : f2(key))

export const hasArea = (f: Filters) => (f.lat !== null && f.lng !== null) || f.where.trim() !== ''
/** The distance in force, in miles (null = any). `home`: a saved home is the area (no place in the filters), so no
 *  distance picked means the home default (QA prod r1: the slider was dead and the list unbounded). */
export const milesOf = (f: Filters, home = false): number | null => f.miles ?? (home && !hasArea(f) && !f.anyDist ? HOME_MILES : null)
export const distanceLabel = (f: Filters, home = false) => { const m = milesOf(f, home); return m === null ? f2('filter.distance_any') : f2('filter.distance_value', { miles: m }) }
/** A distance patch: the slider's end is "Any distance", kept on purpose so a saved home does not snap back to its default. */
export const distancePatch = (miles: number | null): Partial<Filters> => ({ miles, anyDist: miles === null })
export function whenLabel(f: Filters): string {
  if (f.when === 'dates') {
    const r = dateRange(f)
    return r ? formatRange(r.from, r.to) : f2('filter.choose_dates')
  }
  return whenText(WHENS.find(([w]) => w === f.when)?.[1] ?? 'filter.any_time')
}
/** R10: the place label the server resolved a typed where to ("Playa Vista 90094"), once that answer is on screen. */
export function resolvedPlace(f: Filters, centerLabel: string | null): string | null {
  return f.where.trim() && f.lat === null && centerLabel ? centerLabel : null
}
export function areaLabel(f: Filters, resolved: string | null, city: string): string {
  if (resolved) return resolved
  if (f.lat !== null && f.lng !== null) return f2('filter.current_location')
  return f.where.trim() || city
}

/** "Los Angeles · Any distance · Any time · 2 more filters" */
export function summaryLine(f: Filters, area: string, home = false): string {
  const n = extraCount(f)
  return [area, distanceLabel(f, home), whenLabel(f), n ? (n === 1 ? f2('filter.additional_one') : f2('filter.additional_other', { count: n })) : null].filter(Boolean).join(' · ')
}

/**
 * The Where suggestions, ranked like the first-run home field (Alvin 10/08: "drop down autopopulate"): LA County names
 * from the list on the phone first (prefix, then size and fame; laPlace.ts suggestPlaces), then the wider areas and the
 * other cities we know. Eight at most.
 */
// F5: the LA place list (23 KB) is not in the first download; it is fetched when a Where field first shows
let la: typeof import('../../lib/laPlace') | null = null
let laLoading: Promise<void> | null = null
const loadLa = () => (laLoading ??= import('../../lib/laPlace').then((m) => { la = m }).catch(() => { laLoading = null }))

function wherePlaces(t: string): PlaceResult[] {
  const out: PlaceResult[] = (la?.suggestPlaces(t, 6) ?? []).map((p) => (p.label === 'Los Angeles'
    ? { kind: 'la', name: p.label, region: 'California', type: 'City' }
    : { kind: 'near', name: p.label, region: 'Los Angeles', type: 'Neighborhood', area: nearestArea(p.lat, p.lng) }))
  for (const r of searchPlaces(t)) if (!out.some((o) => o.name.toLowerCase() === r.name.toLowerCase())) out.push(r)
  return out.slice(0, 8)
}

// ---------- Where: one field (zip, postal code, neighborhood or city) plus "Use my location" ----------
export function WhereField({ value, onPick, onLocate, locError, big = true, autoFocus = false }: {
  value: string; onPick: (where: string, kind?: string) => void; onLocate: () => void; locError: string | null; big?: boolean; autoFocus?: boolean
}) {
  const [text, setText] = useState(value)
  const [open, setOpen] = useState(false)
  const [hi, setHi] = useState(-1)
  const id = useId()
  useEffect(() => setText(value), [value])
  const [, setLaReady] = useState(la !== null)
  useEffect(() => { if (!la) void loadLa().then(() => setLaReady(la !== null)) }, [])
  // Alvin 10/09: any place can be searched (a zip, another city or country): a Search button on the field searches exactly
  // what they typed, and so does a "Search for" row under the matches (the only row when nothing matches, so Enter on a
  // zip or another city searches it; with matches, Enter still takes the top one)
  const typed = text.trim()
  const matches = open ? wherePlaces(text) : []
  const results: (Omit<PlaceResult, 'kind'> & { kind: PlaceResult['kind'] | 'search' })[] = open && typed && typed !== value.trim() && !matches.some((r) => r.name.toLowerCase() === typed.toLowerCase())
    ? [...matches, { kind: 'search', name: typed, region: '', type: '' }] : matches
  const last = useRef({ w: '', at: 0 }) // a tap fires pointerup and click: pick once
  const pick = (w: string, kind?: string) => {
    if (last.current.w === w && Date.now() - last.current.at < 600) return
    last.current = { w, at: Date.now() }
    setText(w)
    setOpen(false)
    onPick(w, kind)
  }
  return (
    <div className="relative">
      <label htmlFor={`${id}-in`} className="t-eyebrow mb-1.5 block text-ink-2">{f2('filter.where')}</label>
      <div className="relative">
      <div className={`flex items-center gap-2 rounded-[12px] bg-surface px-3.5 ring-1 ring-inset ring-boundary/60 focus-within:ring-2 focus-within:ring-focus ${big ? 'min-h-[52px]' : 'min-h-11'}`}>
        <Search size={18} className="text-ink-2" />
        <input id={`${id}-in`} role="combobox" aria-expanded={results.length > 0} aria-controls={`${id}-list`} aria-autocomplete="list"
          aria-activedescendant={hi >= 0 ? `${id}-o${hi}` : undefined}
          value={text} placeholder={v3('v3.where_placeholder')} autoComplete="off" enterKeyHint="search" autoFocus={autoFocus}
          onChange={(e) => { setText(e.target.value); setOpen(true); setHi(-1) }}
          onFocus={() => setOpen(true)}
          onBlur={() => window.setTimeout(() => setOpen(false), 300) /* long enough for a tap on a suggestion to land (B-045) */}
          onKeyDown={(e) => {
            if (e.key === 'ArrowDown') { e.preventDefault(); setHi((h) => Math.min(results.length - 1, h + 1)) }
            else if (e.key === 'ArrowUp') { e.preventDefault(); setHi((h) => Math.max(-1, h - 1)) }
            else if (e.key === 'Enter') { e.preventDefault(); const r = results[hi >= 0 ? hi : 0]; if (r) pick(r.name, r.kind === 'search' ? undefined : r.kind); else if (typed) pick(typed) /* Enter takes the top match */ }
            else if (e.key === 'Escape') setOpen(false)
          }}
          className="t-body min-w-0 flex-1 bg-transparent py-2 text-ink outline-none placeholder:text-ink-2" />
        <button type="button" data-review-id="where-search" disabled={!typed}
          onMouseDown={(e) => e.preventDefault() /* keep the dropdown's blur from eating the click */}
          onClick={() => { if (typed) pick(typed) }}
          className="press t-control -mr-1.5 inline-flex min-h-9 shrink-0 items-center rounded-[9px] bg-accent px-3 font-semibold text-on-accent disabled:opacity-40">
          {v3('v3.search_button')}
        </button>
      </div>
      {results.length > 0 && (
        <ul id={`${id}-list`} role="listbox" className="material-heavy absolute inset-x-0 top-full z-50 mt-1 max-h-72 overflow-auto rounded-[12px] p-1">
          {results.map((r, i) => (
            <li key={`${r.kind}-${r.name}`} id={`${id}-o${i}`} role="option" aria-selected={i === hi}
              onMouseDown={(e) => e.preventDefault() /* keep the input focused (desktop) */}
              onPointerUp={(e) => { if (e.pointerType !== 'mouse') pick(r.name, r.kind === 'search' ? undefined : r.kind) } /* a tap picks at once (B-045: taps did nothing) */}
              onClick={() => pick(r.name, r.kind === 'search' ? undefined : r.kind)}
              data-review-id={r.kind === 'search' ? 'where-search-row' : undefined}
              className={`flex min-h-11 cursor-pointer flex-col justify-center rounded-[8px] px-3 py-1.5 ${i === hi ? 'bg-sunk' : 'hover:bg-sunk'}`}>
              {r.kind === 'search'
                ? <span className="t-control flex items-center gap-2 text-ink"><Search size={16} className="shrink-0 text-ink-2" />{v3('v3.search_row', { text: r.name })}</span>
                : <><span className="t-control text-ink">{r.name}</span><span className="t-caption text-ink-2">{r.region}</span></>}
            </li>
          ))}
        </ul>
      )}
      </div>
      <button type="button" onClick={onLocate} className="press t-control mt-1 inline-flex min-h-11 items-center gap-1.5 rounded-[12px] px-1 text-accent-ink">
        <Locate size={16} />{f2('filter.use_location')}
      </button>
      {locError && <p role="alert" className="t-meta text-ink-2">{locError}</p>}
    </div>
  )
}

/** Ask for the device location only when the person taps "Use my location". */
export function useLocate(onPoint: (lat: number, lng: number) => void) {
  const [err, setErr] = useState<string | null>(null)
  const locate = () => {
    setErr(null)
    if (!('geolocation' in navigator)) return setErr(f2('filter.location_denied'))
    navigator.geolocation.getCurrentPosition(
      (p) => onPoint(p.coords.latitude, p.coords.longitude),
      () => setErr(f2('filter.location_denied')),
      { timeout: 10000, maximumAge: 300000 },
    )
  }
  return { locate, err }
}

// ---------- Distance: 1-100 miles, then "Any distance" at the end ----------
export function DistanceSlider({ f, area, onChange, onCommit, home = false }: { f: Filters; area: string; onChange: (miles: number | null) => void; onCommit?: () => void; home?: boolean }) {
  const id = useId()
  const on = hasArea(f) || home
  const v = milesOf(f, home) ?? 101
  return (
    <div>
      <div className="flex items-baseline justify-between gap-3">
        <label htmlFor={id} className="t-eyebrow text-ink-2">{f2('filter.distance')}</label>
        <span className="t-control text-ink" aria-hidden="true">{distanceLabel(f, home)}</span>
      </div>
      <input id={id} type="range" min={1} max={101} step={1} value={v} disabled={!on}
        aria-valuetext={distanceLabel(f, home)}
        onChange={(e) => { const n = Number(e.target.value); onChange(n > 100 ? null : n) }}
        onPointerUp={onCommit} onKeyUp={onCommit}
        className="mt-1 h-11 w-full cursor-pointer accent-[var(--accent)] disabled:cursor-not-allowed disabled:opacity-40" />
      <p className="t-caption text-ink-2">{on ? f2('filter.distance_helper', { area }) : f2('filter.where_helper')}</p>
    </div>
  )
}

// ---------- When: single-select chips; "Choose dates" opens a start and an optional end ----------
export function WhenPicker({ f, onChange }: { f: Filters; onChange: (p: Partial<Filters>) => void }) {
  const [from, setFrom] = useState(f.from ?? '')
  const [to, setTo] = useState(f.to ?? '')
  const [choosing, setChoosing] = useState(f.when === 'dates')
  const bad = Boolean(from && to && to < from)
  const today = todayYmd()
  const typed = useMedia('(min-width: 768px)') // R-102 (Round 3 lane 1)
  // B-066 (Alvin pin 566): no "Use these dates" button; picked dates go straight into the filters (the sheet's Show
  // results applies them, the desktop When panel applies them at once)
  const pickDates = (a: string, b: string) => {
    setFrom(a)
    setTo(b)
    if (a && !(b && b < a)) onChange({ when: 'dates', from: a, to: b || null })
  }
  return (
    <fieldset>
      <legend className="t-eyebrow mb-2 text-ink-2">{f2('filter.when')}</legend>
      <Tip>{fb('fb.tip.when')}</Tip>
      <div className="flex flex-wrap gap-2">
        {WHENS.map(([w, key]) => (
          <ToggleChip key={w} on={w === 'dates' ? choosing || f.when === 'dates' : !choosing && f.when === w}
            onToggle={() => { if (w === 'dates') setChoosing(true); else { setChoosing(false); onChange({ when: w, from: null, to: null }) } }}>
            {whenText(key)}
          </ToggleChip>
        ))}
      </div>
      {/* R-102 (B-172, Round 3 lane 1 block): tablet and desktop type their dates; phones keep the native picker below */}
      {choosing && typed && <TypedDates from={from} to={to} today={today} onPick={pickDates} />}
      {choosing && !typed && (
        <div className="mt-3 grid grid-cols-2 gap-3">
          <label className="block">
            <span className="t-caption mb-1 block text-ink-2">{f2('filter.start_date')}</span>
            <input type="date" min={today} value={from} onChange={(e) => pickDates(e.target.value, to)}
              className="t-body min-h-11 w-full rounded-[12px] bg-surface px-3 ring-1 ring-inset ring-boundary/60" />
          </label>
          <label className="block">
            <span className="t-caption mb-1 block text-ink-2">{f2('filter.end_date')}</span>
            <input type="date" min={from || today} value={to} onChange={(e) => pickDates(from, e.target.value)}
              className="t-body min-h-11 w-full rounded-[12px] bg-surface px-3 ring-1 ring-inset ring-boundary/60" />
          </label>
          {bad && <p role="alert" className="t-meta col-span-2 text-error">{f2('filter.date_error')}</p>}
        </div>
      )}
    </fieldset>
  )
}

/** R-15: one plain line under a choice on why it sharpens the picks. */
const Tip = ({ children }: { children: ReactNode }) => <p className="t-caption -mt-1 mb-2 text-ink-2">{children}</p>

/** Everything else (category, free, how well known (R-08) / under the radar, setting, words (R-94)). */
function MoreFilters({ f, onChange }: { f: Filters; onChange: (p: Partial<Filters>) => void }) {
  const fameOn = useFlag('fame_filter')
  const wordsOn = useFlag('words_filter')
  const toggleCat = (c: string) => onChange({ cats: f.cats.includes(c) ? f.cats.filter((x) => x !== c) : [...f.cats, c] })
  const toggleFame = (x: Fame) => onChange({ fame: f.fame.includes(x) ? f.fame.filter((y) => y !== x) : FAMES.filter((y) => y === x || f.fame.includes(y)) })
  return (
    <>
      <fieldset>
        <legend className="t-eyebrow mb-2 text-ink-2">{f2('filter.category')}</legend>
        <Tip>{fb('fb.tip.category')}</Tip>
        <div className="flex flex-wrap gap-2">
          <ToggleChip on={f.cats.length === 0} onToggle={() => onChange({ cats: [] })}>{f2('filter.all_categories')}</ToggleChip>
          {CATS.map((c) => <ToggleChip key={c} on={f.cats.includes(c)} onToggle={() => toggleCat(c)}>{catName(c)}</ToggleChip>)}
        </div>
      </fieldset>
      {fameOn ? (
        <>
          <div>
            <Tip>{fb('fb.tip.price')}</Tip>
            <div className="flex flex-wrap gap-2"><ToggleChip on={f.free} onToggle={() => onChange({ free: !f.free })}>{f2('filter.free')}</ToggleChip></div>
          </div>
          <fieldset data-review-id="fame-filter">
            <legend className="t-eyebrow mb-2 text-ink-2">{fb('fb.fame.legend')}</legend>
            <Tip>{fb('fb.fame.tip')}</Tip>
            <div className="flex flex-wrap gap-2">
              <ToggleChip on={f.fame.includes('well')} onToggle={() => toggleFame('well')}>{fb('fb.fame.well')}</ToggleChip>
              <ToggleChip on={f.fame.includes('local')} onToggle={() => toggleFame('local')}>{fb('fb.fame.local')}</ToggleChip>
              <ToggleChip on={f.hidden} onToggle={() => onChange({ hidden: !f.hidden })}>{fb('fb.fame.radar')}</ToggleChip>
            </div>
          </fieldset>
        </>
      ) : (
        <div className="flex flex-wrap gap-2">
          <ToggleChip on={f.free} onToggle={() => onChange({ free: !f.free })}>{f2('filter.free')}</ToggleChip>
          <ToggleChip on={f.hidden} onToggle={() => onChange({ hidden: !f.hidden })}>{f2('filter.radar')}</ToggleChip>
        </div>
      )}
      <fieldset>
        <legend className="t-eyebrow mb-2 text-ink-2">{f2('filter.setting')}</legend>
        <Tip>{fb('fb.tip.setting')}</Tip>
        <div className="flex flex-wrap gap-2">
          {([[null, 'filter.any_setting'], ['indoor', 'filter.indoors'], ['outdoor', 'filter.outdoors']] as const).map(([v, key]) => (
            <ToggleChip key={key} on={f.setting === v} onToggle={() => onChange({ setting: v })}>{f2(key)}</ToggleChip>
          ))}
        </div>
      </fieldset>
      {wordsOn && <WordsFilter inc={f.inc} exc={f.exc} onChange={onChange} />}
    </>
  )
}

/** The full editor (phone sheet / desktop 480px dialog): changes are a draft until Show results; Close drops them. */
export function FilterEditor({ open, onClose, value, onApply, area, fixedCat, resolved = null, home = null }: {
  open: boolean; onClose: () => void; value: Filters; onApply: (f: Filters) => void; area: string; fixedCat?: string; resolved?: string | null; home?: string | null
}) {
  const [draft, setDraft] = useState(value)
  const [round, setRound] = useState(0)
  useEffect(() => {
    if (open) {
      setDraft(value)
      setRound((r) => r + 1)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps
  const timeOn = useLaneFlag('time_filter') // Round 3, Lane 3 (R-103)
  const patch = (p: Partial<Filters>) => setDraft((d) => ({ ...d, ...p }))
  const { locate, err } = useLocate((lat, lng) => patch({ lat, lng, where: '', miles: draft.miles ?? AREA_MILES }))
  // the place in the draft is the one already applied: show what the server resolved it to
  const samePlace = draft.where === value.where && draft.lat === value.lat && draft.lng === value.lng
  // a saved home is the place when none is typed (QA prod r1: the box was empty and the slider dead)
  const atHome = Boolean(home) && !hasArea(draft)
  const shownWhere = draft.lat !== null ? f2('filter.current_location') : samePlace && resolved ? resolved : draft.where || (atHome ? home! : '')
  const footer = (
    <div className="flex gap-3">
      <button type="button" onClick={() => setDraft({ ...NO_FILTERS, where: draft.where, lat: draft.lat, lng: draft.lng, cats: fixedCat ? [] : [], exc: draft.exc /* hidden words stay hidden; each has its own remove */ })}
        className={buttonClass('secondary', 'lg', 'flex-1')}>{f2('filter.reset')}</button>
      <button type="button" data-review-id="filter-apply" onClick={() => { onApply(draft); onClose() }} className={buttonClass('primary', 'lg', 'flex-[2]')}>{f2('filter.results')}</button>
    </div>
  )
  return (
    <Modal open={open} onClose={onClose} label={f2('filter.open')} title={f2('filter.open')} footer={footer}>
      <div key={round} className="space-y-7 pb-2">
        <WhereField value={shownWhere} onPick={(w, kind) => { if (w !== shownWhere || !samePlace) patch({ where: w, lat: null, lng: null, miles: milesFor(w, kind, draft.miles) }) }} onLocate={locate} locError={err} />
        <DistanceSlider f={draft} home={atHome} area={draft.lat !== null ? f2('filter.current_location') : samePlace ? area : draft.where || area} onChange={(miles) => patch(distancePatch(miles))} />
        <WhenPicker f={draft} onChange={patch} />
        {/* Round 3, Lane 3 (R-103): time of day and weekdays, under When */}
        {timeOn && <TimeFilter f={draft} onChange={patch} />}
        {!fixedCat ? <MoreFilters f={draft} onChange={patch} /> : <MoreFilters f={{ ...draft, cats: [fixedCat] }} onChange={(p) => patch({ ...p, cats: [] })} />}
      </div>
    </Modal>
  )
}

/** A small panel under a quick control (desktop): closes on Escape or a click outside. */
function Popover({ open, onClose, children, className = '' }: { open: boolean; onClose: () => void; children: ReactNode; className?: string }) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!open) return
    const down = (e: PointerEvent) => { if (ref.current && !ref.current.parentElement?.contains(e.target as Node)) onClose() }
    const key = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('pointerdown', down)
    document.addEventListener('keydown', key)
    return () => {
      document.removeEventListener('pointerdown', down)
      document.removeEventListener('keydown', key)
    }
  }, [open, onClose])
  if (!open) return null
  return <div ref={ref} className={`material-heavy absolute left-0 top-full z-50 mt-2 rounded-[20px] p-5 ${className}`}>{children}</div>
}

function QuickButton({ label, value, onClick, expanded, className = '' }: { label: string; value: string; onClick: () => void; expanded?: boolean; className?: string }) {
  return (
    <button type="button" onClick={onClick} aria-expanded={expanded} aria-haspopup="dialog"
      className={`gh-fill press flex min-h-11 w-full items-center justify-center gap-2 rounded-[12px] bg-surface px-2.5 text-left ring-1 ring-inset ring-boundary/60 lg:justify-between lg:px-3.5 ${className}`}>
      <span className="min-w-0">
        <span className="sr-only">{label}: </span>
        <span className="t-control block truncate text-ink">{value}</span>
      </span>
      <ChevronDown size={16} className="hidden text-ink-2 lg:block" />
    </button>
  )
}

/**
 * The filter bar (flow3 §2). Phone: Where across the top, then Distance, When and Filters (Distance and When open the
 * editor). Desktop: one row, Where 400, Distance 240, When 240, Filters; the quick controls commit as they change.
 */
/**
 * `home` (fr3): someone with a saved home sees a compact chip ("Within 10 mi of Playa Vista 90094 · Change") instead of
 * the big Where field and Use my location; tapping it brings the field back.
 */
export function FilterBar({ f, onChange, area, fixedCat, resolved = null, home = null }: { f: Filters; onChange: (p: Partial<Filters>) => void; area: string; fixedCat?: string; resolved?: string | null; home?: string | null }) {
  const desktop = useDesktop()
  const [changing, setChanging] = useState(false)
  const atHome = Boolean(home) && !hasArea(f)
  const chip = atHome && !changing
  const [editor, setEditor] = useState(false)
  const [pop, setPop] = useState<'distance' | 'when' | null>(null)
  const [miles, setMiles] = useState(milesOf(f, atHome))
  useEffect(() => setMiles(milesOf(f, atHome)), [f.miles, f.anyDist, atHome]) // eslint-disable-line react-hooks/exhaustive-deps
  const { locate, err } = useLocate((lat, lng) => onChange({ lat, lng, where: '', miles: f.miles ?? AREA_MILES }))
  const extra = extraCount(f)
  const whereValue = f.lat !== null ? f2('filter.current_location') : resolved ?? (f.where || (atHome ? home! : ''))
  return (
    <div data-review-id="filter-bar" className="flex flex-wrap gap-2 lg:flex-nowrap lg:items-start lg:gap-4">
      <div className="w-full lg:w-[400px] lg:shrink-0">
        {/* picking the label it already shows (Enter on "Playa Vista 90094") keeps the place they typed */}
        {chip ? (
          <button type="button" data-review-id="home-chip" onClick={() => setChanging(true)}
            className="gh-fill press flex min-h-11 w-full items-center gap-2 rounded-[12px] bg-surface px-3.5 text-left ring-1 ring-inset ring-boundary/60 lg:mt-[22px]">
            <Locate size={16} className="shrink-0 text-ink-2" />
            <span className="t-control min-w-0 flex-1 truncate text-ink">
              {home /* the distance sits once, on the Distance button beside it (Plan v3 blind test r2: 'Within 10 mi' showed twice) */}
            </span>
            <span className="t-control shrink-0 text-accent-ink underline underline-offset-4">{f2('home.near_change')}</span>
          </button>
        ) : (
          <WhereField value={whereValue} onPick={(w, kind) => {
            if (atHome && w === home) { setChanging(false); return } // picking home again keeps the home view
            if (!resolved || w !== resolved) onChange({ where: w, lat: null, lng: null, miles: milesFor(w, kind, f.miles) })
          }} onLocate={locate} locError={err} autoFocus={changing} />
        )}
      </div>
      <div className="relative min-w-0 flex-1 lg:mt-[22px] lg:w-[240px] lg:flex-none lg:shrink-0">
        <QuickButton label={f2('filter.distance')} value={distanceLabel(f, atHome)} expanded={pop === 'distance'}
          onClick={() => (desktop ? setPop(pop === 'distance' ? null : 'distance') : setEditor(true))} />
        <Popover open={pop === 'distance'} onClose={() => setPop(null)} className="w-[320px]">
          <DistanceSlider f={{ ...f, ...distancePatch(miles) }} home={atHome} area={area} onChange={setMiles} onCommit={() => onChange(distancePatch(miles))} />
        </Popover>
      </div>
      <div className="relative min-w-0 flex-1 lg:mt-[22px] lg:w-[240px] lg:flex-none lg:shrink-0">
        <QuickButton label={f2('filter.when')} value={whenLabel(f)} expanded={pop === 'when'}
          onClick={() => (desktop ? setPop(pop === 'when' ? null : 'when') : setEditor(true))} />
        <Popover open={pop === 'when'} onClose={() => setPop(null)} className="w-[380px]">
          <WhenPicker f={f} onChange={(p) => { onChange(p); if (p.when !== 'dates') setPop(null) /* dates: stays open for the end date */ }} />
        </Popover>
      </div>
      <div className="shrink-0 lg:mt-[22px] lg:flex-1">
        <button type="button" onClick={() => setEditor(true)}
          className="gh-fill press flex min-h-11 w-full items-center justify-center gap-1.5 rounded-[12px] bg-surface px-2.5 ring-1 ring-inset ring-boundary/60 lg:w-auto">
          <Sliders size={18} />
          <span className="t-control text-ink">{f2('filter.open')}</span>
          {extra > 0 && <span className="t-caption inline-flex size-5 items-center justify-center rounded-full bg-accent font-semibold text-on-accent">{extra}</span>}
        </button>
      </div>
      <FilterEditor open={editor} onClose={() => setEditor(false)} value={f} area={area} fixedCat={fixedCat} resolved={resolved} home={home}
        onApply={(next) => onChange(next)} />
    </div>
  )
}

/** While scrolling: a compact line with the current filters; tapping it opens the full editor. */
export function FilterSummary({ f, area, onChange, fixedCat, visible, resolved = null, home = null }: { f: Filters; area: string; onChange: (p: Partial<Filters>) => void; fixedCat?: string; visible: boolean; resolved?: string | null; home?: string | null }) {
  const [editor, setEditor] = useState(false)
  return (
    <>
      <div data-review-id="filter-summary" aria-hidden={!visible} className={`fixed inset-x-0 top-[calc(61px+env(safe-area-inset-top))] z-20 border-b border-line bg-bg transition-[transform,opacity] duration-200 lg:top-[72px] ${visible ? 'translate-y-0 opacity-100' : 'pointer-events-none -translate-y-full opacity-0'}`}>
        <div className="mx-auto flex h-[52px] w-full max-w-md items-center gap-2 px-5 md:max-w-[704px] md:px-8 lg:max-w-[1376px]">
          <button type="button" tabIndex={visible ? 0 : -1} onClick={() => setEditor(true)} className="press t-meta min-h-11 min-w-0 flex-1 truncate rounded-[12px] text-left text-ink">
            {summaryLine(f, area, Boolean(home) && !hasArea(f))}
          </button>
          <button type="button" tabIndex={visible ? 0 : -1} onClick={() => setEditor(true)} className={buttonClass('secondary', 'sm', 'shrink-0')}>
            <Sliders size={16} />{f2('filter.open')}
          </button>
        </div>
      </div>
      <FilterEditor open={editor} onClose={() => setEditor(false)} value={f} area={area} fixedCat={fixedCat} resolved={resolved} home={home} onApply={(next) => onChange(next)} />
    </>
  )
}
